// Consulta PURA do tipo-base de um atributo de entidade IFC, sobre a
// tabela gerada em ifcAttrTypes.generated.ts. Sem dependências de
// rawIfc / viewerApi / WASM: são dados puros, reutilizáveis pelo futuro
// editor de autoria IDS e pelo pacote partilhado @vn2r/ids.
//
// "Tipo-base" (AttrBaseType) é a categoria do primitivo EXPRESS do
// atributo declarado no esquema: integer | real | string | boolean |
// logical | enum. A tabela guarda só os atributos PRÓPRIOS de cada
// entidade; os herdados resolvem-se aqui seguindo a cadeia `p` até à
// raiz (ex.: Name/Description vêm de IfcRoot).

import {
  IFC_ATTR_TYPES,
  type AttrBaseType,
  type EntityAttrs,
  type IfcAttrSchema,
} from './ifcAttrTypes.generated.js'

export type { AttrBaseType, IfcAttrSchema } from './ifcAttrTypes.generated.js'

const DEFAULT_SCHEMA: IfcAttrSchema = 'IFC4'

/** Normaliza uma string de esquema (ex.: 'ifc4', 'IFC4X3') para uma
 *  chave conhecida; desconhecida -> IFC4 (o mais comum). */
export function normalizeSchema(schema?: string | null): IfcAttrSchema {
  if (!schema) return DEFAULT_SCHEMA
  const u = schema.toUpperCase().trim()
  return (u in IFC_ATTR_TYPES ? u : DEFAULT_SCHEMA) as IfcAttrSchema
}

/** Nome de entidade normalizado para a chave da tabela (MAIÚSCULAS,
 *  sem espaços). Aceita 'IfcWall', 'IFCWALL', 'ifcwall'. */
function entityKey(entity: string): string {
  return entity.toUpperCase().trim()
}

/**
 * Tipo-base do atributo `attribute` da entidade `entity` no esquema
 * dado (default IFC4). Resolve herança seguindo a superclasse. O nome do
 * atributo é comparado sem distinguir maiúsculas/minúsculas. Devolve
 * undefined quando a entidade é desconhecida, o atributo não existe, ou
 * o seu tipo não é primitivo (referência a entidade, lista, select).
 */
export function attributeBaseType(
  entity: string,
  attribute: string,
  schema?: string | null,
): AttrBaseType | undefined {
  const table = IFC_ATTR_TYPES[normalizeSchema(schema)]
  const want = attribute.toLowerCase().trim()
  let cur: string | null = entityKey(entity)
  const guard = new Set<string>()
  while (cur && !guard.has(cur)) {
    guard.add(cur)
    const node: EntityAttrs | undefined = table[cur]
    if (!node) return undefined
    for (const name in node.a) {
      if (name.toLowerCase() === want) return node.a[name]
    }
    cur = node.p
  }
  return undefined
}

/**
 * Todos os atributos de tipo primitivo de uma entidade (próprios +
 * herdados), nome canónico (PascalCase) -> categoria. Um atributo
 * redeclarado numa subclasse ganha (mais específico primeiro).
 */
export function primitiveAttributesOf(
  entity: string,
  schema?: string | null,
): Map<string, AttrBaseType> {
  const table = IFC_ATTR_TYPES[normalizeSchema(schema)]
  const out = new Map<string, AttrBaseType>()
  const seenLower = new Set<string>()
  let cur: string | null = entityKey(entity)
  const guard = new Set<string>()
  while (cur && !guard.has(cur)) {
    guard.add(cur)
    const node: EntityAttrs | undefined = table[cur]
    if (!node) break
    for (const name in node.a) {
      const low = name.toLowerCase()
      if (!seenLower.has(low)) {
        seenLower.add(low)
        out.set(name, node.a[name])
      }
    }
    cur = node.p
  }
  return out
}

/** Uma categoria aceita xs:pattern? Só faz sentido em texto: string e
 *  enum (tokens). Números, booleanos e lógicos não. */
export function acceptsPattern(cat: AttrBaseType): boolean {
  return cat === 'string' || cat === 'enum'
}

/** A entidade é conhecida no esquema dado? */
export function isKnownEntity(entity: string, schema?: string | null): boolean {
  return entityKey(entity) in IFC_ATTR_TYPES[normalizeSchema(schema)]
}

/**
 * `entity` é o `ancestor` ou uma sua subclasse (ex.: IFCWALL é subtipo de
 * IFCPRODUCT)? Segue a cadeia de superclasses da tabela gerada. Entidade
 * desconhecida -> false. Serve para delimitar universos de avaliação
 * ("só os produtos") sem enumerar listas de classes à mão.
 */
export function isSubtypeOf(
  entity: string,
  ancestor: string,
  schema?: string | null,
): boolean {
  const table = IFC_ATTR_TYPES[normalizeSchema(schema)]
  const want = entityKey(ancestor)
  let cur: string | null = entityKey(entity)
  const guard = new Set<string>()
  while (cur && !guard.has(cur)) {
    if (cur === want) return true
    guard.add(cur)
    const node: EntityAttrs | undefined = table[cur]
    if (!node) return false
    cur = node.p
  }
  return false
}
